import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, Bank, Key, CheckCircle, Warning, ArrowSquareOut, Copy } from "@phosphor-icons/react";

export default function PaymentSettings() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setConfig(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (user?.role !== "admin") {
    return <div className="card-tactical p-12 text-center"><p className="text-gray-400">Solo el administrador puede configurar pagos</p></div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  const isLive = config?.stripe_mode === "live";
  const isConfigured = config?.stripe_configured;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="payment-settings-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Configuracion</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Pagos Stripe + BBVA</h1>
      </div>

      {/* Status Card */}
      <div className="card-tactical p-6" data-testid="stripe-status">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <CreditCard size={28} weight="duotone" className={isLive ? "text-green-400" : "text-yellow-400"} />
            <div>
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Estado de Stripe</h3>
              <p className="text-xs text-gray-500">Procesador de pagos para renta de canchas</p>
            </div>
          </div>
          <Badge variant="outline" className={`badge-status ${isLive ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"}`}>
            {isLive ? "PRODUCCION" : "MODO PRUEBA"}
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-[#0A0A0A] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Cuenta BBVA</p>
            <p className="font-['JetBrains_Mono'] text-sm text-white">CLABE: {config?.clabe || "012180015254421972"}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Moneda</p>
            <p className="font-['JetBrains_Mono'] text-sm text-white">MXN (Pesos Mexicanos)</p>
          </div>
        </div>
      </div>

      {/* Setup Guide */}
      <div className="card-tactical p-6" data-testid="setup-guide">
        <div className="flex items-center gap-2 mb-4">
          <Key size={24} weight="duotone" className="text-[#CCFF00]" />
          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase">
            {isLive ? "Stripe Activo" : "Activar Cobros Reales"}
          </h3>
        </div>

        {isLive ? (
          <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30">
            <CheckCircle size={24} className="text-green-400" />
            <div>
              <p className="text-green-400 font-semibold">Stripe esta en modo produccion</p>
              <p className="text-sm text-gray-400">Los cobros son reales y se depositan a tu cuenta BBVA</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30">
              <Warning size={24} className="text-yellow-400" />
              <div>
                <p className="text-yellow-400 font-semibold">Modo de prueba activo</p>
                <p className="text-sm text-gray-400">Los pagos actuales NO son reales. Sigue estos pasos para activar cobros:</p>
              </div>
            </div>

            <div className="space-y-3">
              {[
                { step: 1, title: "Crear cuenta Stripe Mexico", desc: "Ve a stripe.com/mx y crea tu cuenta de negocio", link: "https://dashboard.stripe.com/register", linkText: "Ir a Stripe" },
                { step: 2, title: "Verificar tu negocio", desc: "Proporciona tu RFC, direccion y datos del negocio" },
                { step: 3, title: "Agregar cuenta BBVA", desc: "En Stripe Dashboard > Settings > Linked External Accounts > agrega tu CLABE: 012180015254421972" },
                { step: 4, title: "Obtener API Key de produccion", desc: "En Stripe Dashboard > Developers > API Keys > copia la Secret Key (sk_live_...)" },
                { step: 5, title: "Compartir API Key", desc: "Enviame tu API Key de produccion y la activo en la app. Los cobros empezaran a depositarse en tu BBVA automaticamente." },
              ].map((s) => (
                <div key={s.step} className="flex gap-4 p-3 bg-[#0A0A0A] border border-white/5" data-testid={`step-${s.step}`}>
                  <span className="font-['Barlow_Condensed'] text-2xl font-black text-gray-700 w-8">{s.step}</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">{s.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{s.desc}</p>
                    {s.link && (
                      <a href={s.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#CCFF00] hover:underline mt-1">
                        <ArrowSquareOut size={12} /> {s.linkText}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="bg-[#141414] border border-white/5 p-5 text-sm text-gray-500" data-testid="how-it-works">
        <p className="font-['Barlow_Condensed'] text-xs uppercase tracking-[0.2em] font-semibold text-gray-400 mb-2">Como funciona el flujo de pago</p>
        <ul className="space-y-1 text-xs">
          <li>1. El jugador reserva una cancha y hace clic en "Pagar"</li>
          <li>2. Se abre la pagina de pago segura de Stripe (tarjeta de credito/debito)</li>
          <li>3. Stripe procesa el cobro y confirma la reserva automaticamente</li>
          <li>4. El dinero se deposita en tu cuenta BBVA en 2-3 dias habiles</li>
          <li>5. Puedes ver el historial de transacciones en la seccion "Pagos"</li>
        </ul>
      </div>
    </div>
  );
}
